import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import { useState, useEffect } from 'react';
import { updateSEO, cleanupStructuredData } from '../../lib/seo';

interface Event {
  id: number;
  name: string;
  date: string;
  time: string;
  venue: string;
  location: string;
  city: string;
  state: string;
  description: string;
  image: string;
  ticketUrl: string;
  featured: boolean;
  genre: string;
}

export default function EventsPage() {
  // SEO Meta Tags
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://ranchfireradio.com';
    
    updateSEO({
      title: 'RanchFire Live! Texas Country Music Events, Festivals & Dancehalls | RanchFire Radio',
      description: 'Discover Texas Country music events, festivals, rodeos, and dancehall concerts. RanchFire Live curates the best Red Dirt, Tex-Mex, and Americana live music experiences across Texas including Austin Rodeo, San Antonio Stock Show, Gruene Hall, and more.',
      keywords: 'Texas Country Events, Texas Music Festivals, Red Dirt Concerts, Dancehall Events, Austin Rodeo, San Antonio Rodeo, Gruene Hall, Texas Honky Tonk, Tejano Music Events, RanchFire Live',
      canonical: '/events',
      ogTitle: 'RanchFire Live! Texas Country Music Events & Festivals',
      ogDescription: 'Your guide to Texas Country festivals, dancehalls, rodeos, and live music weekends. Discover authentic Texas music experiences.',
      ogImage: 'https://public.readdy.ai/ai/img_res/c95d64de04a80890aaafe2898bdee1e8.jpg',
      structuredData: {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "RanchFire Live! - Texas Country Music Events",
        "description": "Curated guide to Texas Country music events, festivals, rodeos, and dancehall concerts",
        "url": `${siteUrl}/events`,
        "mainEntity": {
          "@type": "ItemList",
          "name": "Featured Texas Country Events",
          "itemListElement": [
            {
              "@type": "Event",
              "name": "Live at Floore's Country Store",
              "startDate": "2025-06-15T20:00",
              "location": {
                "@type": "Place",
                "name": "Floore's Country Store",
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "Helotes",
                  "addressRegion": "TX",
                  "addressCountry": "US"
                }
              },
              "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode"
            },
            {
              "@type": "Event",
              "name": "Austin Rodeo",
              "startDate": "2026-03-14T19:00",
              "location": {
                "@type": "Place",
                "name": "Travis County Expo Center",
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "Austin",
                  "addressRegion": "TX",
                  "addressCountry": "US"
                }
              },
              "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode"
            },
            {
              "@type": "Event",
              "name": "San Antonio Stock Show & Rodeo",
              "startDate": "2026-02-05T19:30",
              "location": {
                "@type": "Place",
                "name": "AT&T Center",
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "San Antonio",
                  "addressRegion": "TX",
                  "addressCountry": "US"
                }
              },
              "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode"
            },
            {
              "@type": "Event",
              "name": "Tejano Music Awards Fan Fair 2026",
              "startDate": "2026-03-12",
              "location": {
                "@type": "Place",
                "name": "San Antonio Convention Center",
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "San Antonio",
                  "addressRegion": "TX",
                  "addressCountry": "US"
                }
              },
              "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode"
            },
            {
              "@type": "Event",
              "name": "Gruene Hall - Texas' Oldest Dance Hall",
              "startDate": "2025-10-12T19:00",
              "location": {
                "@type": "Place",
                "name": "Gruene Hall",
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "New Braunfels",
                  "addressRegion": "TX",
                  "addressCountry": "US"
                }
              },
              "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode"
            }
          ]
        },
        "publisher": {
          "@type": "Organization",
          "name": "RanchFire Radio",
          "url": siteUrl
        }
      }
    });

    return () => {
      cleanupStructuredData();
    };
  }, []);

  const [weekendEmail, setWeekendEmail] = useState('');
  const [weekendStatus, setWeekendStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isWeekendSubmitting, setIsWeekendSubmitting] = useState(false);

  const [partnerName, setPartnerName] = useState('');
  const [partnerEmail, setPartnerEmail] = useState('');
  const [partnerOrganization, setPartnerOrganization] = useState('');
  const [partnerMessage, setPartnerMessage] = useState('');
  const [partnerStatus, setPartnerStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isPartnerSubmitting, setIsPartnerSubmitting] = useState(false);

  const [eventName, setEventName] = useState('');
  const [eventEmail, setEventEmail] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventVenue, setEventVenue] = useState('');
  const [eventDetails, setEventDetails] = useState('');
  const [eventStatus, setEventStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isEventSubmitting, setIsEventSubmitting] = useState(false);

  const events: Event[] = [
    {
      id: 1,
      name: 'Live at Floore\'s Country Store',
      date: '2025-06-15',
      time: '8:00 PM',
      venue: 'Floore\'s Country Store',
      location: 'Helotes, TX',
      city: 'Helotes',
      state: 'Texas',
      description: 'Experience live music at the legendary Floore\'s Country Store, one of the oldest dance halls in Texas. This iconic venue has hosted country music legends for decades and continues to showcase the best in Texas Country and Americana music in an authentic, intimate setting.',
      image: 'https://static.readdy.ai/image/f407d1ad302e1be1c54e67247a4ef201/56a68e5476a5297ac8783aa2c0cfa01f.jpeg',
      ticketUrl: 'https://liveatfloores.com/upcoming-events/',
      featured: true,
      genre: 'Texas Country'
    },
    {
      id: 2,
      name: 'Monterrey Country Fest 2026',
      date: '2026-05-17',
      time: '6:00 PM',
      venue: 'Parque Fundidora',
      location: 'Monterrey, NL',
      city: 'Monterrey',
      state: 'Nuevo León',
      description: 'Experience the ultimate country music festival in the heart of Monterrey, Mexico. Monterrey Country Fest brings together the best of Texas Country, Red Dirt, and Americana music in a spectacular outdoor setting at the iconic Parque Fundidora, celebrating the cross-border connection of country music culture.',
      image: 'https://readdy.ai/api/search-image?query=massive%20outdoor%20country%20music%20festival%20in%20monterrey%20mexico%20with%20large%20illuminated%20stage%20setup%20against%20mountain%20backdrop%20and%20modern%20urban%20skyline%20vibrant%20sunset%20colors%20with%20orange%20and%20purple%20sky%20thousands%20of%20festival%20attendees%20enjoying%20live%20country%20music%20performance%20spectacular%20lighting%20effects%20and%20professional%20sound%20equipment%20creating%20energetic%20festival%20atmosphere%20in%20iconic%20mexican%20park%20setting&width=800&height=500&seq=103&orientation=landscape',
      ticketUrl: 'https://www.nuevoleon.travel/monterrey-country-fest/',
      featured: true,
      genre: 'Americana'
    },
    {
      id: 3,
      name: 'Tejano Music Awards Fan Fair 2026',
      date: '2026-03-12',
      time: '',
      venue: 'San Antonio Convention Center',
      location: 'San Antonio, TX',
      city: 'San Antonio',
      state: 'Texas',
      description: 'Join us for the ultimate celebration of Tejano music at the Tejano Music Awards Fan Fair. Experience live performances from award-winning artists, meet your favorite musicians, and celebrate the rich cultural heritage of Tejano music in the heart of San Antonio.',
      image: 'https://static.readdy.ai/image/f407d1ad302e1be1c54e67247a4ef201/9461a5e3e68ee774da4a1395a54c881b.jpeg',
      ticketUrl: 'https://tejanomusicawards.com/fanfair/',
      featured: true,
      genre: 'Tejano Country'
    },
    {
      id: 4,
      name: "Gruene Hall- Texas' Oldest Dance Hall",
      date: '2025-10-12',
      time: '7:00 PM',
      venue: 'Gruene Hall',
      location: 'New Braunfels, TX',
      city: 'New Braunfels',
      state: 'Texas',
      description: 'Step back in time at Texas\' oldest dance hall for an authentic honky tonk experience. This multi-day festival features traditional Texas country music, two-stepping lessons, and the kind of genuine atmosphere you can only find in the Hill Country.',
      image: 'https://static.readdy.ai/image/f407d1ad302e1be1c54e67247a4ef201/b5f161a488ab00a8a284528c276d59dc.png',
      ticketUrl: 'https://gruenehall.com/honkytonk/',
      featured: true,
      genre: 'Texas Country'
    },
    {
      id: 5,
      name: 'Outlaw Country Gathering',
      date: '2025-11-08',
      time: '4:00 PM',
      venue: 'Billy Bob\'s Texas',
      location: 'Fort Worth, TX',
      city: 'Fort Worth',
      state: 'Texas',
      description: 'The world\'s largest honky tonk hosts a celebration of outlaw country music. This festival honors the rebellious spirit of country music with performances from artists who keep the outlaw tradition alive and thriving.',
      image: 'https://readdy.ai/api/search-image?query=large%20indoor%20country%20music%20venue%20with%20massive%20crowd%20and%20bright%20stage%20lights%20featuring%20performer%20with%20guitar%20in%20silhouette%20against%20dramatic%20lighting%20authentic%20texas%20honky%20tonk%20atmosphere%20with%20wooden%20beams%20and%20rustic%20decor%20energetic%20concert%20scene&width=800&height=500&seq=6&orientation=landscape',
      ticketUrl: '#',
      featured: false,
      genre: 'Outlaw Country'
    },
    {
      id: 6,
      name: 'Austin Rodeo',
      date: '2026-03-14',
      time: '7:00 PM',
      venue: 'Travis County Expo Center',
      location: 'Austin, TX',
      city: 'Austin',
      state: 'Texas',
      description: 'The Austin Rodeo brings world-class rodeo action and top-tier country music to the Texas capital. Experience thrilling bull riding, barrel racing, and roping competitions alongside live performances from country music stars. A beloved Austin tradition celebrating Western heritage and Texas culture.',
      image: 'https://readdy.ai/api/search-image?query=professional%20rodeo%20arena%20at%20night%20with%20dramatic%20spotlights%20illuminating%20cowboy%20on%20bucking%20bronco%20dust%20flying%20in%20the%20air%20packed%20stadium%20crowd%20cheering%20american%20flags%20waving%20authentic%20western%20rodeo%20atmosphere%20with%20warm%20amber%20and%20golden%20lighting%20texas%20rodeo%20championship%20event&width=800&height=500&seq=austinrodeo01&orientation=landscape',
      ticketUrl: 'https://rodeoaustin.com/',
      featured: true,
      genre: 'Rodeo'
    },
    {
      id: 7,
      name: 'San Antonio Stock Show & Rodeo',
      date: '2026-02-05',
      time: '7:30 PM',
      venue: 'AT&T Center',
      location: 'San Antonio, TX',
      city: 'San Antonio',
      state: 'Texas',
      description: 'One of the largest and most prestigious rodeos in the country, the San Antonio Stock Show & Rodeo features 20 days of world-class rodeo competition, livestock shows, carnival rides, and incredible live music performances. A Texas tradition since 1950 that brings together the best of Western culture and entertainment.',
      image: 'https://readdy.ai/api/search-image?query=massive%20indoor%20rodeo%20arena%20with%20professional%20bull%20rider%20competing%20under%20bright%20stadium%20lights%20huge%20crowd%20in%20attendance%20at%20prestigious%20texas%20rodeo%20event%20american%20western%20atmosphere%20with%20cowboys%20and%20cowgirls%20spectacular%20lighting%20and%20dust%20particles%20in%20air%20championship%20rodeo%20competition&width=800&height=500&seq=sarodeo01&orientation=landscape',
      ticketUrl: 'https://sarodeo.com/',
      featured: true,
      genre: 'Rodeo'
    }
  ];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleWeekendSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsWeekendSubmitting(true);
    setWeekendStatus('idle');

    try {
      const formElement = e.target as HTMLFormElement;
      const formData = new FormData(formElement);
      
      const urlEncodedData = new URLSearchParams();
      formData.forEach((value, key) => {
        urlEncodedData.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d60b270iku00ff9vl23g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString()
      });

      if (response.ok) {
        setWeekendStatus('success');
        setWeekendEmail('');
      } else {
        setWeekendStatus('error');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setWeekendStatus('error');
    } finally {
      setIsWeekendSubmitting(false);
    }
  };

  const handlePartnerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsPartnerSubmitting(true);
    setPartnerStatus('idle');

    try {
      const formElement = e.target as HTMLFormElement;
      const formData = new FormData(formElement);
      
      const urlEncodedData = new URLSearchParams();
      formData.forEach((value, key) => {
        urlEncodedData.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d60b270iku00ff9vl230', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString()
      });

      if (response.ok) {
        setPartnerStatus('success');
        setPartnerName('');
        setPartnerEmail('');
        setPartnerOrganization('');
        setPartnerMessage('');
      } else {
        setPartnerStatus('error');
      }
    } catch (error) {
      console.error('Partner form error:', error);
      setPartnerStatus('error');
    } finally {
      setIsPartnerSubmitting(false);
    }
  };

  const handleEventSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsEventSubmitting(true);
    setEventStatus('idle');

    try {
      const formElement = e.target as HTMLFormElement;
      const formData = new FormData(formElement);
      
      const urlEncodedData = new URLSearchParams();
      formData.forEach((value, key) => {
        urlEncodedData.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d60b270iku00ff9vl240', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString()
      });

      if (response.ok) {
        setEventStatus('success');
        setEventName('');
        setEventEmail('');
        setEventDate('');
        setEventVenue('');
        setEventDetails('');
      } else {
        setEventStatus('error');
      }
    } catch (error) {
      console.error('Event submission error:', error);
      setEventStatus('error');
    } finally {
      setIsEventSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative h-screen min-h-[700px] overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="https://readdy.ai/api/search-image?query=authentic%20texas%20dancehall%20interior%20with%20wooden%20dance%20floor%20and%20stage%20lights%20in%20historic%20ballroom%20setting%20with%20red%20and%20amber%20lighting%20creating%20warm%20atmosphere%20musicians%20performing%20on%20stage%20with%20guitars%20and%20country%20music%20vibe%20vintage%20americana%20aesthetic&width=1920&height=1080&seq=rflive001&orientation=landscape"
            alt="Texas Dancehall"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>

        <div className="relative h-full flex items-center justify-center">
          <div className="max-w-5xl mx-auto px-6 sm:px-8 text-center">
            <h1 className="text-6xl sm:text-7xl lg:text-8xl font-normal text-white mb-4 uppercase tracking-tight drop-shadow-2xl" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>
              RanchFire Live!
            </h1>
            <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-amber-400 mb-8 drop-shadow-lg" style={{ fontFamily: 'Georgia, serif' }}>
              From Backroads and Barstools
            </p>
            <p className="text-lg sm:text-xl lg:text-2xl text-white/95 max-w-4xl mx-auto leading-relaxed drop-shadow-md mb-12">
              Your guide to the festivals, dancehalls, rodeos, and live-music weekends that define RanchFire culture. From Tex-Mex honky-tonks and two-steppin' floors to BBQ smoke, county fairs, and coastal sunsets, RanchFire Live curates events people want to experience.
            </p>
            <a
              href="#upcoming-events"
              className="inline-block bg-amber-700 hover:bg-amber-800 text-white px-10 py-4 text-lg font-bold uppercase tracking-wide transition-all duration-300 shadow-2xl cursor-pointer whitespace-nowrap"
            >
              Upcoming Events
            </a>
          </div>
        </div>
      </section>

      {/* What Is RanchFire Live Section */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-normal text-stone-900 mb-8 uppercase tracking-tight" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>
            What Is RanchFire Live?
          </h2>
          <div className="space-y-6 text-lg sm:text-xl text-stone-700 leading-relaxed">
            <p>
              RanchFire Live is the lifestyle compliment of RanchFire Radio, built for people who plan their weekends around music, travel, family and a good time!
            </p>
            <p className="font-bold text-stone-900">
              We don't list everything. We curate what matters.
            </p>
            <p>
              If it feels like a RanchFire good time, it's here.
            </p>
          </div>
        </div>
      </section>

      {/* What You'll Find Section */}
      <section className="py-20 sm:py-24 bg-stone-100">
        <div className="max-w-6xl mx-auto px-6 sm:px-8">
          <h2 className="text-4xl sm:text-5xl font-normal text-stone-900 mb-12 text-center uppercase tracking-tight" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>
            What You'll Find
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 border-l-4 border-amber-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-live-line text-2xl text-amber-700"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-stone-900 mb-2">Live Events</h3>
                  <p className="text-stone-600 leading-relaxed">Curated picks for concerts, shows, and performances across Texas</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 border-l-4 border-amber-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-music-2-line text-2xl text-amber-700"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-stone-900 mb-2">Festivals</h3>
                  <p className="text-stone-600 leading-relaxed">Major lineups, ticket alerts, and insider festival guides</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 border-l-4 border-amber-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl text-amber-700"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-stone-900 mb-2">Music Scenes</h3>
                  <p className="text-stone-600 leading-relaxed">Deep dives into regional sounds, venues, and emerging artists</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 border-l-4 border-amber-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-t-shirt-line text-2xl text-amber-700"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-stone-900 mb-2">Lifestyle Brands</h3>
                  <p className="text-stone-600 leading-relaxed">Spotlight on Texas Country fashion, gear, and culture</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 border-l-4 border-amber-700 md:col-span-2">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-pulse-line text-2xl text-amber-700"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-stone-900 mb-2">Cultural Momentum</h3>
                  <p className="text-stone-600 leading-relaxed">What's shaping the Red Dirt movement right now</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Different Section */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          <h2 className="text-4xl sm:text-5xl font-normal text-stone-900 mb-12 text-center uppercase tracking-tight" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>
            Why RanchFire Live Is Different
          </h2>
          <div className="space-y-6 text-lg text-stone-700 leading-relaxed">
            <p className="text-xl font-bold text-stone-900">
              Most guides scrape calendars. RanchFire Live lives the weekends.
            </p>
            <ul className="space-y-4 pl-6">
              <li className="flex items-start gap-3">
                <span className="text-amber-700 font-bold">•</span>
                <span>Built by people inside Texas country culture, not chasing trends or clicks</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-700 font-bold">•</span>
                <span>Focus on Texas Country, Red Dirt, Americana, dancehall Tex-Mex culture, live crowds, and working venues</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-700 font-bold">•</span>
                <span>This is the Texas RanchFire Weekend Economy, covered with respect</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Upcoming Events Section */}
      <section id="upcoming-events" className="py-20 sm:py-24 bg-stone-100">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <h2 className="text-4xl sm:text-5xl font-normal text-stone-900 mb-4 text-center uppercase tracking-tight" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>
            Upcoming Events
          </h2>
          <p className="text-center text-stone-600 text-lg mb-12">Featured events happening now</p>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            {events.filter(e => e.featured).map(event => (
              <article 
                key={event.id}
                className="bg-white border-2 border-stone-200 overflow-hidden hover:border-amber-700 transition-all duration-300 group"
              >
                <div className="relative w-full h-64 overflow-hidden">
                  <img 
                    src={event.image}
                    alt={event.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                <div className="p-6 sm:p-8">
                  <h3 className="text-2xl sm:text-3xl font-bold text-stone-900 mb-3 group-hover:text-amber-700 transition-colors" style={{ fontFamily: 'Georgia, serif' }}>
                    {event.name}
                  </h3>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-stone-600">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-calendar-line text-lg"></i>
                      </div>
                      <span className="ml-3 text-sm font-medium">
                        {formatDate(event.date)} at {event.time}
                      </span>
                    </div>
                    <div className="flex items-center text-stone-600">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <span className="ml-3 text-sm font-medium">{event.venue}, {event.location}</span>
                    </div>
                  </div>

                  <p className="text-stone-600 text-sm leading-relaxed mb-6">
                    {event.description}
                  </p>

                  <a
                    href={event.ticketUrl}
                    className="inline-block bg-amber-700 text-white px-8 py-3 font-bold uppercase tracking-wide hover:bg-amber-800 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Get Tickets
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* For Fans Section */}
      <section className="py-20 sm:py-24 bg-gradient-to-br from-amber-700 to-orange-800">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-normal text-white mb-6 uppercase tracking-tight" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>
            For Fans
          </h2>
          <p className="text-xl text-white/95 mb-10 leading-relaxed">
            Plan better weekends. Discover new towns, new venues, and new favorite songs.
          </p>

          {weekendStatus === 'success' && (
            <div className="mb-8 p-4 bg-green-100 border border-green-400 text-green-800 rounded-lg max-w-xl mx-auto">
              <div className="flex items-center justify-center gap-2">
                <i className="ri-checkbox-circle-fill text-xl"></i>
                <span className="font-bold">Welcome to the Fans Newsletter!</span>
              </div>
            </div>
          )}

          {weekendStatus === 'error' && (
            <div className="mb-8 p-4 bg-red-100 border border-red-400 text-red-800 rounded-lg max-w-xl mx-auto">
              <div className="flex items-center justify-center gap-2">
                <i className="ri-error-warning-fill text-xl"></i>
                <span className="font-bold">Something went wrong. Please try again.</span>
              </div>
            </div>
          )}

          <form onSubmit={handleWeekendSubmit} data-readdy-form id="weekend-newsletter-form" className="max-w-xl mx-auto">
            <div className="mb-4">
              <input
                type="email"
                name="email"
                value={weekendEmail}
                onChange={(e) => setWeekendEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="w-full px-6 py-4 text-stone-900 border-2 border-white focus:border-stone-900 focus:outline-none text-lg"
              />
            </div>
            <button
              type="submit"
              disabled={isWeekendSubmitting}
              className="w-full bg-stone-900 text-white px-10 py-4 text-lg font-bold uppercase tracking-wide hover:bg-stone-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isWeekendSubmitting ? 'Joining...' : 'Join the RanchFirelive! Weekend Drop'}
            </button>
            <p className="text-white/90 text-sm mt-4">
              Join the Fans Newsletter for weekly Texas RanchFire Weekend guides
            </p>
          </form>
        </div>
      </section>

      {/* For Venues Section */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8">
          <h2 className="text-4xl sm:text-5xl font-normal text-stone-900 mb-6 text-center uppercase tracking-tight" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>
            For Venues, Festivals & Promoters
          </h2>
          <p className="text-xl text-stone-700 mb-12 text-center max-w-3xl mx-auto leading-relaxed">
            Partner with RanchFire Live to reach an audience that actually shows up.
          </p>

          <div className="bg-stone-100 p-8 sm:p-12 mb-12">
            <h3 className="text-2xl font-bold text-stone-900 mb-6">What We Offer:</h3>
            <ul className="space-y-3 text-lg text-stone-700">
              <li className="flex items-start gap-3">
                <span className="text-amber-700 font-bold">•</span>
                <span>Event spotlights and weekend takeovers</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-700 font-bold">•</span>
                <span>Playlist and radio tie-ins</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-700 font-bold">•</span>
                <span>Newsletter features</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-700 font-bold">•</span>
                <span>On-site coverage</span>
              </li>
            </ul>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Submit Event Form */}
            <div className="bg-stone-50 p-8 border-2 border-stone-200">
              <h3 className="text-2xl font-bold text-stone-900 mb-6">Submit an Event</h3>
              
              {eventStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <i className="ri-checkbox-circle-fill text-xl"></i>
                    <span className="font-bold">Event submitted successfully!</span>
                  </div>
                </div>
              )}

              {eventStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <i className="ri-error-warning-fill text-xl"></i>
                    <span className="font-bold">Something went wrong. Please try again.</span>
                  </div>
                </div>
              )}

              <form onSubmit={handleEventSubmit} data-readdy-form id="submit-event-form" className="space-y-4">
                <div>
                  <label htmlFor="event-name" className="block text-stone-700 font-bold text-sm mb-2">
                    Event Name *
                  </label>
                  <input
                    type="text"
                    id="event-name"
                    name="event_name"
                    value={eventName}
                    onChange={(e) => setEventName(e.target.value)}
                    required
                    className="w-full px-4 py-3 text-stone-900 border border-stone-300 focus:border-amber-700 focus:outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="event-email" className="block text-stone-700 font-bold text-sm mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="event-email"
                    name="email"
                    value={eventEmail}
                    onChange={(e) => setEventEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 text-stone-900 border border-stone-300 focus:border-amber-700 focus:outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="event-date" className="block text-stone-700 font-bold text-sm mb-2">
                    Event Date *
                  </label>
                  <input
                    type="date"
                    id="event-date"
                    name="event_date"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                    required
                    className="w-full px-4 py-3 text-stone-900 border border-stone-300 focus:border-amber-700 focus:outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="event-venue" className="block text-stone-700 font-bold text-sm mb-2">
                    Venue/Location *
                  </label>
                  <input
                    type="text"
                    id="event-venue"
                    name="venue"
                    value={eventVenue}
                    onChange={(e) => setEventVenue(e.target.value)}
                    required
                    className="w-full px-4 py-3 text-stone-900 border border-stone-300 focus:border-amber-700 focus:outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="event-details" className="block text-stone-700 font-bold text-sm mb-2">
                    Event Details *
                  </label>
                  <textarea
                    id="event-details"
                    name="event_details"
                    value={eventDetails}
                    onChange={(e) => setEventDetails(e.target.value)}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 text-stone-900 border border-stone-300 focus:border-amber-700 focus:outline-none resize-none"
                  ></textarea>
                  <p className="text-xs text-stone-500 mt-1">{eventDetails.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={isEventSubmitting}
                  className="w-full bg-amber-700 text-white px-8 py-3 font-bold uppercase tracking-wide hover:bg-amber-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isEventSubmitting ? 'Submitting...' : 'Submit Event'}
                </button>
              </form>
            </div>

            {/* Partner Form */}
            <div className="bg-stone-50 p-8 border-2 border-stone-200">
              <h3 className="text-2xl font-bold text-stone-900 mb-6">Partner With RanchFire Live!</h3>
              
              {partnerStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <i className="ri-checkbox-circle-fill text-xl"></i>
                    <span className="font-bold">Thanks! We'll be in touch soon.</span>
                  </div>
                </div>
              )}

              {partnerStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <i className="ri-error-warning-fill text-xl"></i>
                    <span className="font-bold">Something went wrong. Please try again.</span>
                  </div>
                </div>
              )}

              <form onSubmit={handlePartnerSubmit} data-readdy-form id="partner-form" className="space-y-4">
                <div>
                  <label htmlFor="partner-name" className="block text-stone-700 font-bold text-sm mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="partner-name"
                    name="name"
                    value={partnerName}
                    onChange={(e) => setPartnerName(e.target.value)}
                    required
                    className="w-full px-4 py-3 text-stone-900 border border-stone-300 focus:border-amber-700 focus:outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="partner-email" className="block text-stone-700 font-bold text-sm mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="partner-email"
                    name="email"
                    value={partnerEmail}
                    onChange={(e) => setPartnerEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 text-stone-900 border border-stone-300 focus:border-amber-700 focus:outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="partner-organization" className="block text-stone-700 font-bold text-sm mb-2">
                    Organization/Venue *
                  </label>
                  <input
                    type="text"
                    id="partner-organization"
                    name="organization"
                    value={partnerOrganization}
                    onChange={(e) => setPartnerOrganization(e.target.value)}
                    required
                    className="w-full px-4 py-3 text-stone-900 border border-stone-300 focus:border-amber-700 focus:outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="partner-message" className="block text-stone-700 font-bold text-sm mb-2">
                    Message *
                  </label>
                  <textarea
                    id="partner-message"
                    name="message"
                    value={partnerMessage}
                    onChange={(e) => setPartnerMessage(e.target.value)}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 text-stone-900 border border-stone-300 focus:border-amber-700 focus:outline-none resize-none"
                  ></textarea>
                  <p className="text-xs text-stone-500 mt-1">{partnerMessage.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={isPartnerSubmitting}
                  className="w-full bg-amber-700 text-white px-8 py-3 font-bold uppercase tracking-wide hover:bg-amber-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPartnerSubmitting ? 'Sending...' : 'Get In Touch'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Powered By Section */}
      <section className="py-20 sm:py-24 bg-stone-900">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-normal text-white mb-6 uppercase tracking-tight" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>
            Powered by RanchFire
          </h2>
          <p className="text-xl text-stone-300 mb-8 leading-relaxed">
            RanchFire Live is a vertical of RanchFire Radio, a Texas-rooted media platform celebrating live music, dancehall culture, and weekend traditions.
          </p>
          <p className="text-2xl font-bold text-amber-400" style={{ fontFamily: 'Georgia, serif' }}>
            Rooted. Real. Country.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-amber-100">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="text-center space-y-6">
            <div>
              <p className="text-stone-900 font-bold text-lg mb-2">Texas-based</p>
              <p className="text-stone-700">Live Music • Dancehalls • Weekends</p>
            </div>
            
            <div>
              <p className="text-stone-700">
                Email: <a href="mailto:info@ranchfireradio.com" className="text-amber-700 hover:text-amber-800 font-bold cursor-pointer">info@ranchfireradio.com</a>
              </p>
            </div>

            <div className="flex justify-center gap-4">
              <a 
                href="https://www.instagram.com/ranchfire_radio/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-700 hover:bg-amber-800 text-white transition-all duration-300 cursor-pointer"
              >
                <i className="ri-instagram-line text-lg"></i>
              </a>
              <a 
                href="#" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-700 hover:bg-amber-800 text-white transition-all duration-300 cursor-pointer"
              >
                <i className="ri-spotify-fill text-lg"></i>
              </a>
            </div>

            <div className="pt-6 border-t border-stone-300">
              <p className="text-stone-600 text-sm">
                2026 © RanchFire Radio a division of RanchFire Multimedia, LLC
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}